import { cn } from "@/common/helpers/utils";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger, TooltipVariant } from "@/components/ui/tooltip";
import { type LucideIcon } from "lucide-react";

export enum BadgeColor {
  Primary = "primary",
  Secondary = "secondary",
  Success = "success",
  Warning = "warning",
  Error = "error",
}

export enum BadgeSize {
  Sm = "sm",
  Md = "md",
}

export type CustomBadgeColor = {
  background: string;
  foreground: string;
  tooltipVariant?: TooltipVariant;
  tooltipClassName?: string;
};

const BADGE_COLOR_CLASSES: Record<BadgeColor, { background: string; foreground: string }> = {
  [BadgeColor.Primary]: { background: "bg-primary/10", foreground: "text-primary" },
  [BadgeColor.Secondary]: { background: "bg-secondary/10", foreground: "text-secondary" },
  [BadgeColor.Success]: { background: "bg-success/10", foreground: "text-success" },
  [BadgeColor.Warning]: { background: "bg-warning/10", foreground: "text-warning" },
  [BadgeColor.Error]: { background: "bg-error/10", foreground: "text-error" },
};

const BADGE_COLOR_TOOLTIP_VARIANT: Record<BadgeColor, TooltipVariant> = {
  [BadgeColor.Primary]: TooltipVariant.OutlinedPrimary,
  [BadgeColor.Secondary]: TooltipVariant.OutlinedSecondary,
  [BadgeColor.Success]: TooltipVariant.OutlinedSuccess,
  [BadgeColor.Warning]: TooltipVariant.OutlinedWarning,
  [BadgeColor.Error]: TooltipVariant.OutlinedError,
};

const BADGE_SIZE_CLASSES: Record<BadgeSize, { circle: string; icon: string }> = {
  [BadgeSize.Sm]: { circle: "h-6 w-6", icon: "h-3.5 w-3.5" },
  [BadgeSize.Md]: { circle: "h-10 w-10", icon: "h-5 w-5" },
};

interface IconBadgeProps {
  icon: LucideIcon;
  color: BadgeColor | CustomBadgeColor;
  tooltip: string;
  size?: BadgeSize;
}

export function IconBadge({ icon: Icon, color, tooltip, size = BadgeSize.Sm }: IconBadgeProps) {
  const isPreset = typeof color === "string";
  const background = isPreset ? BADGE_COLOR_CLASSES[color].background : color.background;
  const foreground = isPreset ? BADGE_COLOR_CLASSES[color].foreground : color.foreground;
  const { circle, icon: iconSize } = BADGE_SIZE_CLASSES[size];

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className={cn("flex shrink-0 items-center justify-center rounded-full", circle, background)}>
            <Icon className={cn(iconSize, foreground)} />
          </div>
        </TooltipTrigger>
        {isPreset ? (
          <TooltipContent variant={BADGE_COLOR_TOOLTIP_VARIANT[color]}>
            <p>{tooltip}</p>
          </TooltipContent>
        ) : (
          <TooltipContent
            variant={color.tooltipVariant}
            className={color.tooltipVariant ? undefined : color.tooltipClassName}
          >
            <p className="max-w-xs">{tooltip}</p>
          </TooltipContent>
        )}
      </Tooltip>
    </TooltipProvider>
  );
}
